import { useState, useEffect, useRef, useMemo, useDeferredValue } from 'react';
import { Task, FullProject, ProjectMetadata } from '../../types';
import { getContactVorgangDisplayName } from '../../components/communication/personModel';
import { matchesTaskFilter } from '../../domain/taskFilterEngine';
import { isExcludedContactOrUndatedVorgang } from '../../domain/projectTaskAggregator';
import {
  hasTaskDate,
  isContactVorgangView,
  isFolderTaskView,
  isIdeaTaskView,
} from '../../domain/taskGuards';
import {
  SearchCategoryType,
  buildProjectsByIdMap,
  buildTasksByIdMap,
  buildProjectAndNodeSearchMatches,
  computeCombinedSearchResults,
  extractUniqueWorkspaceAssignees,
} from './searchMetadataAndHolidayMatchers';

interface UseTaskFilterAndSearchEngineParams {
  tasks: Task[];
  allTasks: Task[];
  currentProjectId?: string;
  allProjects: FullProject[];
  globalMetadata: ProjectMetadata;
  activeWorkspace?: 'professional' | 'private';
  viewMode: string;
  showCompleted: boolean;
  showArchived: boolean;
  filterAssignee: string;
  filterType: string;
  filterStatus: string;
  filterPriority: string;
  searchQuery: string;
  searchMode: 'filter' | 'highlight';
  searchCategory: SearchCategoryType;
  activeSearchTaskId: string | number | null;
  setActiveSearchTaskId: (id: string | number | null) => void;
  setSearchIndex: (idx: number) => void;
}

interface WorkerQueryState {
  query: string;
  matchedIds?: string[];
  results?: Task[];
  indexVersion: number;
}

export function useTaskFilterAndSearchEngine({
  tasks,
  allTasks,
  currentProjectId,
  allProjects,
  globalMetadata,
  activeWorkspace,
  viewMode,
  showCompleted,
  showArchived,
  filterAssignee,
  filterType,
  filterStatus,
  filterPriority,
  searchQuery,
  searchMode,
  searchCategory,
  activeSearchTaskId,
  setActiveSearchTaskId,
  setSearchIndex,
}: UseTaskFilterAndSearchEngineParams) {
  const visibleTasksRef = useRef<Task[]>([]);
  const searchResultsRef = useRef<Task[]>([]);
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const isMindmapMode = viewMode === 'mindmap';

  // Phase 4 · Schritt 4.3: Precomputed O(1) lookup maps for projects and tasks
  const projectsByIdMap = useMemo(() => buildProjectsByIdMap(allProjects), [allProjects]);
  const allTasksByIdMap = useMemo(() => buildTasksByIdMap(allTasks), [allTasks]);

  const baseHierarchicalTasks = useMemo(() => {
    const filtered = tasks
      .filter((t) => {
        if (!isMindmapMode && isIdeaTaskView(t)) return false;
        if (isExcludedContactOrUndatedVorgang(t)) return false;
        const effectiveName = getContactVorgangDisplayName(t);
        if (!effectiveName && !t.isInlineNew) return false;

        return matchesTaskFilter(
          t,
          {
            showArchived,
            showCompleted,
            filterAssignee,
            filterStatus,
            filterType,
            filterPriority,
            searchQuery: deferredSearchQuery,
            searchMode,
            includeDescriptionInSearch: true,
          },
          { effectiveName }
        );
      })
      .map((t) => {
        if (!(t.name || '').trim() && isContactVorgangView(t)) {
          return { ...t, name: getContactVorgangDisplayName(t) };
        }
        return t;
      });

    const idMap = new Map<string, Task>();
    const childrenMap = new Map<string, Task[]>();
    const rootTasks: Task[] = [];

    for (let i = 0; i < filtered.length; i++) {
      idMap.set(String(filtered[i].id), filtered[i]);
    }

    // M-6: In Mindmap mode, preserve the ancestor chain (parent folders/nodes) for any matched child task
    if (isMindmapMode && filtered.length > 0) {
      const rawTasksById = new Map<string, Task>();
      for (let i = 0; i < tasks.length; i++) {
        rawTasksById.set(String(tasks[i].id), tasks[i]);
      }
      for (let i = 0; i < filtered.length; i++) {
        let currParentId = filtered[i].parentId;
        const visited = new Set<string>([String(filtered[i].id)]);
        while (currParentId !== undefined && currParentId !== null) {
          const parentKey = String(currParentId);
          if (visited.has(parentKey) || idMap.has(parentKey)) break;
          visited.add(parentKey);
          const ancestor = rawTasksById.get(parentKey);
          if (!ancestor || (!showArchived && ancestor.archived)) break;
          idMap.set(parentKey, ancestor);
          currParentId = ancestor.parentId;
        }
      }
    }

    const hierarchicalPool = Array.from(idMap.values());
    for (let i = 0; i < hierarchicalPool.length; i++) {
      const t = hierarchicalPool[i];
      const parentKey = t.parentId !== undefined && t.parentId !== null ? String(t.parentId) : '';

      if (isMindmapMode && parentKey && idMap.has(parentKey)) {
        let list = childrenMap.get(parentKey);
        if (!list) {
          list = [];
          childrenMap.set(parentKey, list);
        }
        list.push(t);
      } else {
        rootTasks.push(t);
      }
    }

    const sortTasks = (a: Task, b: Task) => {
      if (a.start && b.start) {
        if (a.start !== b.start) return a.start < b.start ? -1 : 1;
      } else if (a.start) {
        return -1;
      } else if (b.start) {
        return 1;
      }

      if (a.sortOrder !== b.sortOrder) {
        return (a.sortOrder || 0) - (b.sortOrder || 0);
      }

      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
    };

    const result: Task[] = [];
    const flatten = (t: Task, depth: number) => {
      const children = childrenMap.get(String(t.id));
      result.push({
        ...t,
        _depth: depth,
        _hasChildren: !!(children && children.length > 0),
        _childCount: children?.length || 0,
      });

      if (children) {
        children.sort(sortTasks).forEach((child) => flatten(child, depth + 1));
      }
    };

    rootTasks.sort(sortTasks).forEach((root) => flatten(root, 0));

    return result;
  }, [
    tasks,
    showCompleted,
    showArchived,
    filterAssignee,
    filterType,
    filterStatus,
    filterPriority,
    deferredSearchQuery,
    isMindmapMode,
    searchMode,
  ]);

  const visibleTasks = useMemo(() => {
    return baseHierarchicalTasks
      .filter((t) => {
        if (!isMindmapMode && (isFolderTaskView(t) || isIdeaTaskView(t))) return false;
        if (['gantt', 'calendar', 'timeline'].includes(viewMode)) {
          return hasTaskDate(t);
        }
        return true;
      })
      .map((t) => {
        if (['gantt', 'calendar', 'timeline'].includes(viewMode) && (!t.start || !t.end)) {
          return {
            ...t,
            start: t.start || t.end,
            end: t.end || t.start,
          };
        }
        return t;
      });
  }, [baseHierarchicalTasks, viewMode, isMindmapMode]);

  useEffect(() => {
    visibleTasksRef.current = visibleTasks;
  }, [visibleTasks]);

  const [workerQueryState, setWorkerQueryState] = useState<WorkerQueryState>({
    query: '',
    matchedIds: [],
    indexVersion: 0,
  });
  const workerRef = useRef<Worker | null>(null);
  const lastDispatchedQueryRef = useRef<{ query: string; indexVersion: number }>({
    query: '',
    indexVersion: 0,
  });
  const indexVersionRef = useRef(0);

  useEffect(() => {
    if (typeof Worker === 'undefined') return;
    try {
      workerRef.current = new Worker(new URL('../../workers/search.worker.ts', import.meta.url), {
        type: 'module',
      });
      workerRef.current.onmessage = (e) => {
        const rawData = e.data;
        const resolvedQuery =
          typeof rawData?.query === 'string'
            ? rawData.query
            : lastDispatchedQueryRef.current.query;

        if (Array.isArray(rawData?.matchedIds)) {
          setWorkerQueryState({
            query: resolvedQuery,
            matchedIds: rawData.matchedIds,
            indexVersion: lastDispatchedQueryRef.current.indexVersion,
          });
          return;
        }

        const results: Task[] = Array.isArray(rawData) ? rawData : rawData?.results || [];
        setWorkerQueryState({
          query: resolvedQuery,
          results,
          indexVersion: lastDispatchedQueryRef.current.indexVersion,
        });
      };
      return () => {
        workerRef.current?.terminate();
      };
    } catch (err) {
      console.warn(
        '[useAppState] Could not initialize search worker, falling back to sync search.',
        err
      );
    }
  }, []);

  useEffect(() => {
    indexVersionRef.current += 1;
    if (workerRef.current) {
      workerRef.current.postMessage({
        type: 'INDEX_TASKS',
        allTasks,
      });
    }
  }, [allTasks]);

  useEffect(() => {
    const normalizedQuery = deferredSearchQuery.trim().toLowerCase();
    if (workerRef.current && normalizedQuery) {
      lastDispatchedQueryRef.current = {
        query: normalizedQuery,
        indexVersion: indexVersionRef.current,
      };
      workerRef.current.postMessage({
        type: 'QUERY',
        query: deferredSearchQuery,
        currentProjectId,
      });
    } else if (!normalizedQuery) {
      setWorkerQueryState((prev) =>
        prev.query === '' && (!prev.matchedIds || prev.matchedIds.length === 0)
          ? prev
          : { query: '', matchedIds: [], indexVersion: indexVersionRef.current }
      );
    }
  }, [deferredSearchQuery, currentProjectId, allTasks]);

  const projectAndNodeMatches = useMemo(
    () =>
      buildProjectAndNodeSearchMatches({
        query: deferredSearchQuery,
        allProjects,
        globalMetadata,
        activeWorkspace,
        showArchived,
      }),
    [deferredSearchQuery, allProjects, globalMetadata, activeWorkspace, showArchived]
  );

  const searchResults = useMemo(() => {
    const q = deferredSearchQuery.trim().toLowerCase();
    if (!q) return [];

    const hasFreshWorkerResults =
      workerQueryState.query === q &&
      workerQueryState.indexVersion === indexVersionRef.current &&
      workerRef.current !== null;

    let candidateTasks: Task[] = allTasks;
    if (hasFreshWorkerResults) {
      if (Array.isArray(workerQueryState.matchedIds)) {
        const resolved: Task[] = [];
        for (const id of workerQueryState.matchedIds) {
          const found = allTasksByIdMap.get(String(id));
          if (found) resolved.push(found);
        }
        candidateTasks = resolved;
      } else if (Array.isArray(workerQueryState.results)) {
        candidateTasks = workerQueryState.results;
      }
    }

    return computeCombinedSearchResults({
      query: deferredSearchQuery,
      searchCategory,
      showArchived,
      projectAndNodeMatches,
      candidateTasks,
      hasFreshWorkerResults,
      projectsByIdMap,
    });
  }, [
    deferredSearchQuery,
    allTasks,
    allTasksByIdMap,
    projectAndNodeMatches,
    searchCategory,
    showArchived,
    projectsByIdMap,
    workerQueryState,
  ]);

  useEffect(() => {
    searchResultsRef.current = searchResults;
  }, [searchResults]);

  useEffect(() => {
    if (searchResults.length > 0) {
      const hasActive = searchResults.some((t) => String(t.id) === String(activeSearchTaskId));
      if (!hasActive) {
        setActiveSearchTaskId(searchResults[0].id);
        setSearchIndex(0);
      }
    } else if (activeSearchTaskId !== null) {
      setActiveSearchTaskId(null);
      setSearchIndex(0);
    }
  }, [searchResults, activeSearchTaskId, setActiveSearchTaskId, setSearchIndex]);

  const searchMatchIds = useMemo(
    () => new Set(searchResults.map((t) => String(t.id))),
    [searchResults]
  );
  const activeSearchMatchId = activeSearchTaskId;

  const allAssignees = useMemo(
    () => extractUniqueWorkspaceAssignees(tasks, allProjects, activeWorkspace),
    [tasks, allProjects, activeWorkspace]
  );

  return {
    visibleTasks,
    visibleTasksRef,
    searchResults,
    searchMatchIds,
    activeSearchMatchId,
    allAssignees,
  };
}
